import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, ChevronDown } from "lucide-react";
import clsx from "clsx";
import { useToggle } from "react-use";
import type { SearchSourcesData } from "@/types/open-chat";
import { getFaviconUrl } from "@/helpers/images";
import { safeExternalUrl } from "@/helpers/safeExternalUrl";

interface Props {
  searchSources?: SearchSourcesData;
  isSearching: boolean;
}

export const SearchResultsBox = ({ searchSources, isSearching }: Props) => {
  const [isExpanded, toggleIsExpanded] = useToggle(true);

  const sources = searchSources?.sources ?? [];
  const hasSources = sources.length > 0;
  const displayQuery = searchSources?.search_queries?.[0] ?? "Searching...";

  React.useEffect(() => {
    if (!isSearching && hasSources) {
      const timer = setTimeout(() => toggleIsExpanded(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [isSearching, hasSources, toggleIsExpanded]);

  return (
    <div className="mb-3 w-full">
      <button
        type="button"
        disabled={!hasSources}
        aria-expanded={hasSources ? isExpanded : undefined}
        onClick={() => toggleIsExpanded()}
        className={clsx(
          "flex w-full items-center gap-2 py-1.5 text-sm text-muted-foreground",
          hasSources ? "cursor-pointer" : "cursor-default",
        )}
      >
        {isSearching && !hasSources ? (
          <motion.div
            animate={{ opacity: [0.4, 1, 0.4] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
          >
            <Globe size={15} className="text-blue-500" />
          </motion.div>
        ) : (
          <Globe size={15} className="text-muted-foreground" />
        )}
        <span className="truncate">{displayQuery}</span>
        {hasSources && (
          <>
            <span className="ml-auto shrink-0 text-xs text-muted-foreground/70">{sources.length} results</span>
            <ChevronDown
              size={14}
              className={clsx("shrink-0 transition-transform duration-200", isExpanded && "rotate-180")}
            />
          </>
        )}
      </button>

      <AnimatePresence initial={false}>
        {isExpanded && hasSources && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="max-h-48 overflow-y-auto rounded-lg border border-border bg-muted/30">
              {sources.map(source => {
                const safeUrl = safeExternalUrl(source.url);
                return (
                  <a
                    key={source.id}
                    href={safeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between gap-3 border-b border-border/50 px-3 py-2 text-sm last:border-b-0 hover:bg-muted/50"
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      {source.title && (
                        <img
                          src={getFaviconUrl(safeUrl ?? null, 16) ?? undefined}
                          alt=""
                          width={16}
                          height={16}
                          className="shrink-0"
                          loading="lazy"
                        />
                      )}
                      <span className="truncate text-foreground">{source.title ?? safeUrl}</span>
                    </div>
                    {source.title && <span className="shrink-0 text-xs text-muted-foreground">{source.title}</span>}
                  </a>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
